import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingActions from '@/pages/home/components/FloatingActions';

const facility = {
  slug: 'printing-dyeing',
  name: 'Kiddo Outfit.',
  tagline: 'Advanced Surface Decoration & Coloration',
  description: 'State-of-the-art printing and dyeing facility offering screen printing, digital printing, sublimation, fabric dyeing, and specialized wash effects — giving apparel brands unmatched creative flexibility.',
  longDescription: 'Modern Print & Dye Ltd. is the Beaumonde Tex group\'s dedicated surface-decoration and coloration facility. We handle everything from solid fabric dyeing to complex multi-color prints across all fabric types. With 20 print lines including high-speed rotary screen, DTG digital, and sublimation technologies — plus comprehensive dyeing and specialty washing capabilities — we give apparel brands the creative firepower to execute any surface design. Our in-house color lab with spectrophotometer matching ensures exact color consistency across production runs. Monthly capacity of 300,000 pieces, 350+ skilled technicians, and integrated ERP with upstream garment facilities.',
  heroImage: 'https://readdy.ai/api/search-image?query=Modern%20textile%20digital%20printing%20facility%20with%20large%20format%20printers%20producing%20vibrant%20colorful%20fabric%2C%20clean%20industrial%20space%2C%20organized%20workstation%20with%20fabric%20samples%20on%20display%2C%20professional%20printing%20industry%20photography%2C%20bright%20natural%20and%20artificial%20lighting%2C%20creative%20atmosphere&width=1800&height=800&seq=printing-hero-individual&orientation=landscape',
  capacityStats: [
    { value: '80,000', label: 'Sq. Ft. Facility', icon: 'ri-building-4-line' },
    { value: '350+', label: 'Skilled Workforce', icon: 'ri-team-line' },
    { value: '20', label: 'Print Lines', icon: 'ri-printer-line' },
    { value: '300,000', label: 'Monthly Capacity (Pcs)', icon: 'ri-bar-chart-line' },
    { value: '12', label: 'Dyeing Machines', icon: 'ri-test-tube-line' },
    { value: '6', label: 'Wash Machines', icon: 'ri-drop-line' },
  ],
  machines: [
    { name: 'Rotary Screen Printers', qty: '4', spec: 'Stork & Zimmer, up to 12 colors', desc: 'High-speed rotary screen printing for continuous yardage and piece goods.' },
    { name: 'DTG Digital Printers', qty: '6', spec: 'Kornit Avalanche & Atlas', desc: 'Direct-to-garment digital printing for complex photorealistic designs on finished garments.' },
    { name: 'Sublimation Printers', qty: '4', spec: 'Mimaki & Epson wide format', desc: 'Full-color dye-sublimation printing for polyester and performance fabric applications.' },
    { name: 'Fabric Dyeing Vessels', qty: '12', spec: 'Thies, 50-500 kg capacity', desc: 'Reactive, pigment, and disperse dyeing with automated dosing and precise temperature control.' },
    { name: 'Industrial Washers', qty: '6', spec: 'Tonello & Ellis, 100-400 kg', desc: 'Enzyme, acid, pigment, silicone, and ozone wash treatments for garment finishing.' },
    { name: 'Laser Etching Systems', qty: '3', spec: 'Jeanologia & GFK laser', desc: 'Precision laser distressing, etching, and decorative patterns on denim and woven garments.' },
  ],
  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=Large%20format%20digital%20textile%20printer%20producing%20vibrant%20multicolor%20fabric%20print%2C%20modern%20clean%20facility%2C%20bright%20industrial%20lighting%2C%20creative%20atmosphere%2C%20professional%20printing%20industry%20photography&width=700&height=500&seq=printing-gallery-01&orientation=landscape',
      alt: 'Digital Printing Bay',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Screen%20printing%20workshop%20with%20multiple%20stations%20and%20colorful%20ink%20displays%2C%20frames%20and%20squeegees%2C%20organized%20industrial%20workspace%2C%20professional%20photography%2C%20bright%20creative%20atmosphere&width=700&height=500&seq=printing-gallery-02&orientation=landscape',
      alt: 'Screen Printing Workshop',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Industrial%20fabric%20dyeing%20facility%20with%20stainless%20steel%20vessels%20and%20color%20lab%2C%20organized%20chemical%20storage%2C%20clean%20modern%20environment%2C%20bright%20lighting%2C%20professional%20textile%20industry%20photography&width=700&height=500&seq=printing-gallery-03&orientation=landscape',
      alt: 'Fabric Dyeing Section',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Color%20matching%20laboratory%20with%20spectrophotometer%20equipment%20and%20fabric%20swatches%2C%20clean%20organized%20workspace%2C%20bright%20lighting%2C%20professional%20textile%20lab%20photography&width=700&height=500&seq=printing-gallery-04&orientation=landscape',
      alt: 'Color Lab',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Industrial%20garment%20washing%20facility%20with%20large%20washing%20machines%20and%20dryers%2C%20clean%20organized%20workspace%2C%20modern%20laundry%20equipment%2C%20bright%20lighting%2C%20professional%20textile%20finishing%20photography&width=700&height=500&seq=printing-gallery-05&orientation=landscape',
      alt: 'Wash House',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Laser%20etching%20machine%20processing%20denim%20fabric%20with%20precision%20patterns%2C%20modern%20clean%20industrial%20equipment%2C%20focused%20detailed%20work%2C%20professional%20manufacturing%20photography&width=700&height=500&seq=printing-gallery-06&orientation=landscape',
      alt: 'Laser Etching Station',
    },
  ],
  capabilities: [
    { icon: 'ri-pantone-line', title: 'Screen Printing', desc: 'High-speed rotary and flatbed screen printing up to 12 colors with vibrant durable application.' },
    { icon: 'ri-computer-line', title: 'Digital Printing', desc: 'DTG and digital fabric printing for complex photorealistic designs with no minimum order quantity.' },
    { icon: 'ri-contrast-2-line', title: 'Sublimation', desc: 'Full-color dye-sublimation for polyester activewear, sportswear, and performance garments.' },
    { icon: 'ri-test-tube-line', title: 'Fabric Dyeing', desc: 'Reactive, pigment, and disperse dyeing for cotton, polyester, blends, and specialty fabrics.' },
    { icon: 'ri-drop-line', title: 'Garment Washing', desc: 'Enzyme, acid, pigment, silicone, and ozone washes for hand-feel and visual effects.' },
    { icon: 'ri-flashlight-line', title: 'Laser Finishing', desc: 'Precision laser etching, distressing, and decorative patterns for denim and woven garments.' },
    { icon: 'ri-brush-line', title: 'Special Effects', desc: 'Foil, flock, puff, high-density, and discharge printing for premium decorative effects.' },
    { icon: 'ri-paint-line', title: 'Tinting', desc: 'Garment tinting and over-dyeing for unique color effects and vintage aesthetics.' },
  ],
};

export default function PrintingDyeingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 },
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[500px] md:h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${facility.heroImage})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/25"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 pb-14 md:pb-20">
          <button
            onClick={() => navigate('/sister-concerns')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors duration-200 mb-5 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            All Sister Concerns
          </button>
          <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold font-heading mb-4">
           Kids
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
            {facility.name}
          </h1>
        </div>
      </section>

      {/* <section className="relative -mt-10 z-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-[#E6EAF0] p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {facility.capacityStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-secondary-50 mb-2">
                  <i className={`${stat.icon} text-secondary-500 text-base`}></i>
                </div>
                <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</div>
                <div className="text-[10px] text-foreground-500 mt-1 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pt-16 pb-14 md:pt-24 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="lg:w-3/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-secondary-500 mb-4">About the Facility</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 leading-tight">
                From Blank Fabric to Branded Art.
              </h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-5">{facility.longDescription}</p>
            </div>
            <div className="lg:w-2/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0]">
                <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                  <i className="ri-settings-3-line text-secondary-500"></i>
                  Machine & Equipment
                </h3>
                <div className="space-y-4">
                  {facility.machines.map((m) => (
                    <div key={m.name} className="border-b border-[#E6EAF0] pb-3 last:border-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="text-sm font-semibold text-foreground-800">{m.name}</p>
                        <span className="text-xs font-bold text-secondary-500 whitespace-nowrap">{m.qty} units</span>
                      </div>
                      <p className="text-[11px] text-foreground-500 mb-1">{m.spec}</p>
                      <p className="text-xs text-foreground-400 leading-snug">{m.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-secondary-500 mb-4">Inside the Facility</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Photo Tour</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facility.gallery.map((img, i) => (
              <div
                key={img.alt}
                className="rounded-lg overflow-hidden border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img src={img.src} alt={img.alt} className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="bg-white px-4 py-3">
                  <p className="text-sm font-medium text-foreground-700">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-secondary-500 mb-4">What We Do</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Print & Dye Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {facility.capabilities.map((cap, i) => (
              <div
                key={cap.title}
                className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 hover:border-secondary-200"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-50 mb-4">
                  <i className={`${cap.icon} text-secondary-500 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{cap.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-secondary-500">
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Bring Your Designs to Life</h2>
          <p className="text-white/80 max-w-[500px] mx-auto mb-8 text-sm md:text-base leading-relaxed">
            From concept to fabric, our printing and dyeing experts turn your creative vision into reality — with precision, speed, and stunning results.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/#contact')}
              className="inline-flex items-center px-8 py-3.5 bg-white text-secondary-600 text-sm font-semibold font-heading rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Send an Inquiry
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button
              onClick={() => navigate('/sister-concerns')}
              className="inline-flex items-center px-8 py-3.5 bg-transparent border-2 border-white/30 text-white text-sm font-semibold font-heading rounded-md hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
            >
              All Sister Concerns
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}